<template>
    <UserPageLayout>
        <div style="margin-bottom: 2rem">
            <h1 class="system-text-color text-center text-3xl">{{ $t('order_header') }}</h1>
            <div v-if="orders.length || subscriptions.length">
                <p v-if="activeSubscriptionCount" class="text-xl text-center system-text-color">
                    {{
                        $t('order_description_subscription', {
                            activeSubscriptionCount: activeSubscriptionCount,
                            activeLimitPlayer: activeLimitPlayer
                        })
                    }}
                </p>

                <p v-if="activeOrderCount" class="text-xl text-center system-text-color">
                    {{
                        $t('order_description_count', {
                            activeOrderCount: activeOrderCount,
                            s: activeOrderCount > 1 ? 's' : '',
                            activeLimitPlayer: activeLimitPlayer
                        })
                    }}
                </p>
            </div>
            <p v-else class="text-xl text-center system-text-color">{{ $t('order_no_order') }}</p>
        </div>

        <div class="max-w-xl m-auto">
            <div v-if="orders.length" class="order-list">
                <h2 class="system-text-color text-center text-xl">{{ $t('order_description') }}</h2>

                <div class="order-row-head hidden md:block">
                    <div class="order-row text-dark-charcoal system-border mb-2">
                        <div class="order-column font-bold">{{ $t('order_table_date') }}</div>
                        <div class="payment-column font-bold">{{ $t('order_table_paid') }}</div>
                        <div class="amount-column font-bold">{{ $t('order_table_amount') }}</div>
                        <div class="quiz-column font-bold">{{ $t('order_table_quiz') }}</div>
                        <div class="expire-column font-bold">{{ $t('order_table_expire') }}</div>
                        <div class="status-column font-bold"></div>
                        <div class="receipt-column font-bold text-center">{{ $t('order_table_receipt') }}</div>
                    </div>
                </div>
                <div
                    v-for="order in orders"
                    :key="order._id.$oid"
                    class="order-row text-dark-charcoal system-border flex mb-2"
                >
                    <div class="order-column">
                        <span class="inline md:hidden font-bold shadow-text-none">{{ $t('order_table_date') }}: </span>
                        {{ order.created_at }}
                    </div>
                    <div class="payment-column">
                        <span class="inline md:hidden font-bold shadow-text-none">{{ $t('order_table_paid') }}: </span>
                        <span class="capitalize shadow-text-none">
                            {{ order.method == 'stripe-checkout' ? 'Stripe' : order.method }}
                        </span>
                    </div>
                    <div class="amount-column">
                        <span class="inline md:hidden font-bold shadow-text-none">
                            {{ $t('order_table_amount') }}:
                        </span>
                        <span class="mr-2 shadow-text-none font-bold">
                            <template v-if="order.currency && order.currency === 'NOK'">
                                {{ moneyNokFormat(order.amount) }}
                            </template>
                            <template v-else>
                                {{ moneyUsdFormat(order.amount) }}
                            </template>
                        </span>
                        <small v-if="order.type != 'buy-credit'" class="md:block">
                            ({{ $t('order_table_players', { limitPlayer: order.data ? order.data.limit_player : 0 }) }})
                        </small>
                        <small v-if="order.type == 'buy-credit'" class="block">(Buy Credit)</small>
                    </div>
                    <div class="quiz-column">
                        <span class="inline md:hidden font-bold shadow-text-none">{{ $t('order_table_quiz') }}: </span>
                        {{ order.quiz.title }}
                    </div>
                    <div class="expire-column">
                        <span class="inline md:hidden font-bold shadow-text-none">
                            {{ $t('order_table_expire') }}:
                        </span>
                        <span v-if="order.buy_player_status == 'suspend'" class="text-red shadow-text-none">
                            {{ $t('order_table_suspended') }}
                        </span>
                        <span v-else class="shadow-text-none">{{ order.expires_at }}</span>
                    </div>
                    <div class="status-column">
                        <span class="inline md:hidden font-bold shadow-text-none">
                            {{ $t('order_table_status') }}:
                        </span>
                        <i
                            :class="[
                                'fas fa-dot-circle shadow-text-none',
                                order.is_active ? 'text-green' : 'text-grey'
                            ]"
                        ></i>
                    </div>
                    <div class="receipt-column">
                        <span v-if="order.method != 'credit'" class="inline md:hidden font-bold shadow-text-none">
                            {{ $t('order_table_receipt') }}:
                        </span>
                        <button
                            v-if="order.method != 'credit'"
                            @click="goToInvoice(order._id.$oid)"
                            class="btn system-button-outline text-sm font-opensans ml-2"
                        >
                            {{ $t('order_table_receipt') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="orderAiQuestions.length" class="order-list">
                <h2 class="system-text-color text-center text-xl">{{ $t('order_description_ai_question') }}</h2>
                <div class="order-row-head hidden md:block">
                    <div class="order-row text-dark-charcoal system-border mb-2" style="justify-content: space-between">
                        <div class="order-column font-bold">{{ $t('order_table_date') }}</div>
                        <div class="payment-column ai font-bold text-center">{{ $t('order_table_paid') }}</div>
                        <div class="amount-column font-bold">{{ $t('order_table_amount') }}</div>
                        <div class="receipt-column font-bold text-center">{{ $t('order_table_receipt') }}</div>
                    </div>
                </div>
                <div
                    v-for="order in orderAiQuestions"
                    :key="order._id.$oid"
                    style="justify-content: space-between"
                    class="order-row text-dark-charcoal system-border flex mb-2"
                >
                    <div class="order-column">
                        <span class="inline md:hidden font-bold shadow-text-none">{{ $t('order_table_date') }}: </span>
                        {{ order.created_at }}
                    </div>
                    <div class="payment-column ai text-center">
                        <span class="inline md:hidden font-bold shadow-text-none">{{ $t('order_table_paid') }}: </span>
                        <span class="capitalize shadow-text-none">
                            {{ order.method == 'stripe-checkout' ? 'Stripe' : order.method }}
                        </span>
                    </div>
                    <div class="amount-column">
                        <span class="inline md:hidden font-bold shadow-text-none">
                            {{ $t('order_table_amount') }}:
                        </span>
                        <span class="mr-2 shadow-text-none font-bold">
                            <template v-if="order.currency && order.currency === 'NOK'">
                                {{ moneyNokFormat(order.amount) }}
                            </template>
                            <template v-else>
                                {{ moneyUsdFormat(order.amount) }}
                            </template>
                        </span>
                        <small class="md:block">
                            ({{
                                $t('order_table_ai_question', {
                                    limit: order.data ? order.data.limit || order.data.limit_player : 0
                                })
                            }})
                        </small>
                    </div>
                    <div class="receipt-column">
                        <span v-if="order.method != 'credit'" class="inline md:hidden font-bold shadow-text-none">
                            {{ $t('order_table_receipt') }}:
                        </span>
                        <button
                            v-if="order.method != 'credit'"
                            @click="goToInvoice(order._id.$oid)"
                            class="btn system-button-outline text-sm font-opensans ml-2"
                        >
                            {{ $t('order_table_receipt') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="subscriptions.length || subscriptionPendings.length" class="order-list">
                <h2 v-if="subscriptions.length" class="system-text-color text-center text-xl">
                    {{ $t('order_header_subscription') }}
                </h2>

                <div
                    v-for="subscription in subscriptions"
                    :key="subscription._id.$oid"
                    class="order-row text-dark-charcoal system-border flex mb-2"
                >
                    <div v-if="subscription.plan" class="plan-name-column text-center">
                        <p class="font-bold text-xl shadow-text-none">{{ subscription.plan.name }}</p>
                        <small>
                            {{
                                $t('order_subscription_desc', {
                                    amount: displayAmount(subscription),
                                    frequency: toLower(subscription.plan.frequency),
                                    player: subscription.plan.player
                                })
                            }}
                        </small>
                    </div>
                    <div class="plan-payment-column flex justify-center items-center p-4 md:p-0">
                        {{ $t('order_table_paid') }}:
                        <span class="ml-2 capitalize shadow-text-none">
                            {{ subscription.method == 'stripe-checkout' ? 'Stripe' : subscription.method }}
                        </span>
                    </div>
                    <div class="plan-payment-column flex justify-center items-center p-4 md:p-0">
                        <button
                            @click="openInvoice(subscription._id.$oid)"
                            class="btn system-button-outline text-sm font-opensans ml-2"
                        >
                            {{ $t('order_button_invoice') }}
                        </button>
                    </div>
                    <div class="plan-action-column p-2 md:p-0 flex justify-center md:justify-end md:items-center">
                        <div
                            v-if="subscription.subscription_status == 'active'"
                            class="flex justify-center items-center"
                        >
                            <Popper :options="{ placement: 'top' }">
                                <div class="popper">
                                    <span v-if="subscription.can_unsubscribe">
                                        {{ $t('order_popup_can_unsubscribe') }}
                                    </span>
                                    <span v-else>
                                        {{
                                            $t('order_popup_unsubscribe', {
                                                unsubscribeDate: subscription.unsubscribe_date
                                            })
                                        }}
                                    </span>
                                </div>
                                <div slot="reference">
                                    <i class="fas fa-info-circle shadow-text-none"></i>
                                </div>
                            </Popper>
                            <button
                                @click="
                                    subscription.method == 'paypal'
                                        ? unsubscribe(subscription.order_id)
                                        : subscription.method == 'vipps'
                                        ? vippsUnsubscribe(subscription.order_id)
                                        : stripeUnsubscribe(subscription.order_id)
                                "
                                :disabled="!subscription.can_unsubscribe"
                                class="btn system-button-color system-button-text-color text-sm font-opensans ml-2"
                            >
                                {{ $t('order_button_unsubscribe') }}
                            </button>
                        </div>
                        <p v-else>
                            <i
                                :class="[
                                    'fas fa-dot-circle shadow-text-none',
                                    subscription.is_active ? 'text-green' : 'text-grey'
                                ]"
                            ></i>
                            <span class="text-sm ml-2">
                                {{
                                    $t('order_available', {
                                        expire: subscription.expires_at
                                            ? subscription.expires_at.split(' ').shift()
                                            : ''
                                    })
                                }}
                            </span>
                        </p>
                    </div>
                </div>

                <h2 class="system-text-color text-center text-xl mt-8" v-if="subscriptionPendings.length">
                    {{ $t('order_header_subscription_status_pending') }}
                </h2>

                <div
                    v-for="subscriptionOrder in subscriptionPendings"
                    :key="subscriptionOrder._id.$oid"
                    class="order-row text-dark-charcoal system-border flex mb-2"
                >
                    <div v-if="subscriptionOrder.plan" class="plan-name-column text-center">
                        <p class="font-bold text-xl shadow-text-none">{{ subscriptionOrder.plan.name }}</p>
                        <small>
                            {{
                                $t('order_subscription_desc', {
                                    amount: displayAmount(subscriptionOrder),
                                    frequency: toLower(subscriptionOrder.plan.frequency),
                                    player: subscriptionOrder.plan.player
                                })
                            }}
                        </small>
                    </div>
                    <div class="plan-payment-column flex justify-center items-center p-4 md:p-0">
                        {{ $t('order_table_paid') }}:
                        <span class="ml-2 capitalize shadow-text-none">
                            {{ subscriptionOrder.method }}
                        </span>
                    </div>
                    <div class="plan-action-column p-2 md:p-0 flex justify-center md:justify-end md:items-center">
                        <div class="flex justify-center items-center">
                            <button
                                @click="onRefreshOrder(subscriptionOrder)"
                                class="btn system-button-color system-button-text-color text-sm font-opensans ml-2"
                            >
                                {{ $t('order_button_refresh') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-center p-6 mb-6">
                <div v-if="isStudent" class="text-center system-text-color mb-5">
                    <div
                        class="school-info inline-flex flex md:flex-row flex-col items-center rounded-lg md:px-10 px-3 py-3"
                        style="border: 2px solid var(--ui-primary-color)">
                        <i class="fas fa-exclamation-circle mr-2"></i>
                        {{ $t('overlay_ask_for_premium_access') }}
                    </div>
                </div>

                <p  v-if="!isStudent" v-html="$t('order_description_see_available')" class="mb-4 system-text-color"></p>
                <router-link
                    v-if="!isStudent"
                    :to="{ name: 'purchase' }"
                    class="system-button-color system-button-text-color btn font-opensans text-xl"
                >
                    {{ $t('order_button_subscribe') }}
                </router-link>
            </div>
        </div>
    </UserPageLayout>
</template>

<script>
import Request from '@/lib/request'
import UserService from '@/lib/user'
import { currentUser } from '@/lib/user'
import appConfig from '@/app.config'
import UserPageLayout from '@/layouts/UserPage'
import popup from '@/lib/popup'
import helperMixins from '@/mixins/helper.js'
import Popper from 'vue-popperjs'

export default {
    metaInfo() {
        return {
            title: this.$t('title_my_orders')
        }
    },

    components: {
        UserPageLayout,
        Popper
    },

    mixins: [helperMixins],

    data() {
        return {
            orders: [],
            subscriptions: [],
            activeLimitPlayer: 0,
            subscriptionPendings: [],
            orderAiQuestions: []
        }
    },

    mounted() {
        this.fetchOrder()
        this.fetchSubscription()
        this.fetchSubscriptionPending()
        this.fetchLimitPlayer()
        this.$store.dispatch('validateSubscription')

        document.body.style.overflow = 'auto'
    },

    computed: {
        countMsg() {
            const orderUnit = this.orders.length > 1 ? 'orders' : 'order'
            const subscriptionUnit = this.subscriptions.length > 1 ? 'subscriptions' : 'subscription'
            const orderMsg = this.orders.length ? `${this.orders.length} ${orderUnit}` : ''
            const subscriptionMsg = this.subscriptions.length
                ? `${this.subscriptions.length} ${subscriptionUnit}`
                : 'no subscription'

            return subscriptionMsg + (this.orders.length ? ` and ${orderMsg}` : '')
        },

        count() {
            return this.orders.length + this.subscriptions.length
        },

        activeSubscriptionCount() {
            return this.subscriptions.length
        },

        activeOrderCount() {
            return this.orders.filter(order => order.is_active).length
        },

        isStudent() {
            return currentUser().role.includes('student')
        }
    },

    methods: {
        goToInvoice(id) {
            this.$store.commit('toggleMainLoader', true)
            const request = this.createRequest()

            request
                .get(appConfig.api.getInvoicePayment.replace(/__id__/, id))
                .then(res => {
                    const data = res.data.data

                    const baseUrl = process.env.VUE_APP_ROOT_URL
                    this.$store.commit('toggleMainLoader', false)
                    return window.open(`${baseUrl}${data.path}`, '_blank')
                })
                .catch(() => {
                    this.$store.commit('toggleMainLoader', false)
                    this.$router.push({ name: 'order' })
                })
        },

        onRefreshOrder(order) {
            var request = new Request()
            const userService = new UserService()

            this.$store.commit('toggleMainLoader', true)

            var payload = {
                user: userService.getUser(),
                plan_id: order.plan_id,
                id_empotency_key: order.id_empotency_key
            }

            request.post(appConfig.api.vippsCreateSubscriptionOrder, payload).then(res => {
                this.fetchSubscription()
                this.fetchSubscriptionPending()

                if (res.data.data.subscription_data_status !== 'ACTIVE') {
                    popup.alert(
                        this.$t('order_alert_refresh_message', { status: res.data.data.subscription_data_status })
                    )
                }

                this.$store.commit('toggleMainLoader', false)
            })
        },

        createRequest() {
            const userService = new UserService()
            const token = userService.getToken()

            return new Request({
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
        },

        fetchOrder() {
            const request = this.createRequest()
            this.$store.commit('toggleMainLoader', true)

            request
                .get(appConfig.api.fetchUserOrder)
                .then(res => {
                    const data = res.data.data

                    this.orders = data.filter(item => {
                        return item.type != 'ai-question'
                    })

                    this.orderAiQuestions = data.filter(item => {
                        return item.type == 'ai-question'
                    })

                    this.$store.commit('toggleMainLoader', false)
                })
                .catch(() => {
                    this.$store.commit('toggleMainLoader', false)
                })
        },

        fetchSubscriptionPending() {
            const request = this.createRequest()

            this.$store.commit('toggleMainLoader', true)

            request
                .get(appConfig.api.vippsSubscriptionPending)
                .then(res => {
                    this.subscriptionPendings = res.data.data
                    this.$store.commit('toggleMainLoader', false)
                })
                .catch(() => {
                    this.$store.commit('toggleMainLoader', false)
                })
        },

        fetchSubscription() {
            const request = this.createRequest()

            this.$store.commit('toggleMainLoader', true)

            request
                .get(appConfig.api.fetchUserSubScription)
                .then(res => {
                    this.subscriptions = res.data.data
                    this.$store.commit('toggleMainLoader', false)
                })
                .catch(() => {
                    this.$store.commit('toggleMainLoader', false)
                })
        },

        vippsUnsubscribe(orderId) {
            const request = this.createRequest()

            popup.confirm(this.$t('order_alert_unsubscribe'), () => {
                this.$store.commit('toggleMainLoader', true)

                request
                    .post(appConfig.api.vippsUnsubscribe, { order_id: orderId })
                    .then(() => {
                        popup.alert(this.$t('order_alert_subscription_canceled'), () => {
                            window.location.reload()
                        })

                        this.$store.commit('toggleMainLoader', false)
                    })
                    .catch(() => {
                        this.$store.commit('toggleMainLoader', false)
                    })
            })
        },

        stripeUnsubscribe(orderId) {
            const request = this.createRequest()

            popup.confirm(this.$t('order_alert_unsubscribe'), () => {
                this.$store.commit('toggleMainLoader', true)

                request
                    .post(appConfig.api.stripeUnsubscribe, { order_id: orderId })
                    .then(() => {
                        popup.alert(this.$t('order_alert_subscription_canceled'), () => {
                            window.location.reload()
                        })

                        this.$store.commit('toggleMainLoader', false)
                    })
                    .catch(() => {
                        this.$store.commit('toggleMainLoader', false)
                    })
            })
        },

        unsubscribe(orderId) {
            const request = this.createRequest()

            popup.confirm(this.$t('order_alert_unsubscribe'), () => {
                this.$store.commit('toggleMainLoader', true)

                request
                    .post(appConfig.api.unsubscribe, { order_id: orderId })
                    .then(() => {
                        popup.alert(this.$t('order_alert_subscription_canceled'), () => {
                            window.location.reload()
                        })

                        this.$store.commit('toggleMainLoader', false)
                    })
                    .catch(() => {
                        this.$store.commit('toggleMainLoader', false)
                    })
            })
        },

        fetchLimitPlayer() {
            const request = this.createRequest()

            request.get(appConfig.api.limitPlayer).then(res => {
                if (res.data != null) {
                    this.activeLimitPlayer = res.data.data.limit
                }
            })
        },

        toLower(msg) {
            return msg.toLowerCase()
        },

        openInvoice(id) {
            this.$router.push({ name: 'invoice', params: { id: id } })
        },

        displayAmount(subscription) {
            if (subscription.currency && subscription.currency === 'NOK') {
                return this.moneyNokFormat(subscription.amount)
            }

            return this.moneyUsdFormat(subscription.plan.amount)
        }
    }
}
</script>

<style lang="scss">
.order-list {
    // background-color: #ededed;
    padding: 0.5rem;
    border-radius: 5px;
    margin-bottom: 1.5rem;

    .order-row {
        background-color: #fff;
        padding: 1rem;
        border-radius: 5px;
        display: flex;

        .order-column {
            width: 15%;
        }
        .payment-column {
            width: 12%;
        }
        .amount-column {
            width: 13%;
        }
        .quiz-column {
            width: 20%;
            text-align: left;
        }

        .status-column {
            width: 5%;
            text-align: center;
        }

        .receipt-column {
            width: 20%;
        }

        .expire-column {
            width: 15%;
        }

        .plan-name-column,
        .plan-payment-column,
        .plan-action-column {
            width: 33%;
        }
    }

    @media screen and (max-width: 767px) {
        .order-row {
            flex-direction: column;
            .order-column {
                width: 100%;
                padding: 5px;
            }
            .payment-column {
                width: 100%;
                padding: 5px;
            }

            .payment-column.ai {
                text-align: left;
            }

            .amount-column {
                width: 100%;
                padding: 5px;
            }
            .quiz-column {
                width: 100%;
                padding: 5px;
            }

            .status-column {
                width: 100%;
                text-align: left;
                padding: 5px;
            }

            .expire-column {
                padding: 5px;
                width: 100%;
            }

            .receipt-column {
                width: 100%;
                padding: 5px;
            }

            .plan-name-column,
            .plan-payment-column,
            .plan-action-column {
                width: 100%;
            }
        }
    }
}
</style>
